import React, { useState } from 'react';
import { Filter, Calendar, Save, X } from 'lucide-react';


const FilterSidebar = ({ filters, onFilterChange, userActivity }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [savedFilters, setSavedFilters] = useState([]);
  const [filterName, setFilterName] = useState('');

  const actionTypes = ['CREATE', 'UPDATE', 'DELETE', 'VIEW', 'EXPORT', 'LOGIN', 'LOGOUT'];
  const entityTypes = ['TRANSACTION', 'EXPENSE', 'NEGOTIATION', 'RECEIVABLE', 'CLIENT', 'SUPPLIER', 'PAYMENT'];
  const severityLevels = ['INFO', 'WARNING', 'ERROR', 'CRITICAL'];

  const handleCheckboxChange = (category, value) => {
    const currentValues = localFilters?.[category] || [];
    const newValues = currentValues?.includes(value)
      ? currentValues?.filter(v => v !== value)
      : [...currentValues, value];
    
    setLocalFilters({ ...localFilters, [category]: newValues });
  };

  const handleDateChange = (field, value) => {
    setLocalFilters({ ...localFilters, [field]: value });
  };

  const applyFilters = () => {
    onFilterChange(localFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      startDate: null,
      endDate: null,
      actionType: [],
      entityType: [],
      severityLevel: [],
      userEmail: ''
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const saveFilter = () => {
    if (!filterName?.trim()) return;
    
    const newFilter = {
      id: Date.now(),
      name: filterName,
      filters: localFilters
    };
    setSavedFilters([...savedFilters, newFilter]);
    setFilterName('');
  };

  const loadSavedFilter = (savedFilter) => {
    setLocalFilters(savedFilter?.filters);
    onFilterChange(savedFilter?.filters);
  };

  const deleteSavedFilter = (filterId) => {
    setSavedFilters(savedFilters?.filter(f => f?.id !== filterId));
  };

  return (
    <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros Avançados
          </h2>
          <button
            onClick={clearFilters}
            className="text-sm text-red-600 hover:text-red-700"
          >
            Limpar
          </button>
        </div>
        
        <button
          onClick={applyFilters}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Aplicar Filtros
        </button>
      </div>
      {/* Date Range Filter */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Período
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Data Inicial</label>
            <input
              type="date"
              value={localFilters?.startDate || ''}
              onChange={(e) => handleDateChange('startDate', e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Data Final</label>
            <input
              type="date"
              value={localFilters?.endDate || ''}
              onChange={(e) => handleDateChange('endDate', e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
      {/* Action Type Filter */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Tipo de Ação</h3>
        <div className="space-y-2">
          {actionTypes?.map(type => (
            <label key={type} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded">
              <input
                type="checkbox"
                checked={localFilters?.actionType?.includes(type) || false}
                onChange={() => handleCheckboxChange('actionType', type)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700">{type}</span>
            </label>
          ))}
        </div>
      </div>
      {/* Entity Type Filter */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Tipo de Entidade</h3>
        <div className="space-y-2">
          {entityTypes?.map(type => (
            <label key={type} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded">
              <input
                type="checkbox"
                checked={localFilters?.entityType?.includes(type) || false}
                onChange={() => handleCheckboxChange('entityType', type)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700">{type}</span>
            </label>
          ))}
        </div>
      </div>
      {/* Severity Level Filter */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Nível de Severidade</h3>
        <div className="space-y-2">
          {severityLevels?.map(level => (
            <label key={level} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded">
              <input
                type="checkbox"
                checked={localFilters?.severityLevel?.includes(level) || false}
                onChange={() => handleCheckboxChange('severityLevel', level)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700">{level}</span>
            </label>
          ))}
        </div>
      </div>
      {/* Save Filter Preset */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Save className="w-4 h-4" />
          Salvar Filtro
        </h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={filterName}
            onChange={(e) => setFilterName(e?.target?.value)}
            placeholder="Nome do filtro"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={saveFilter}
            disabled={!filterName?.trim()}
            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* Saved Filters */}
      {savedFilters?.length > 0 && (
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Filtros Salvos</h3>
          <div className="space-y-2">
            {savedFilters?.map(saved => (
              <div key={saved?.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <button
                  onClick={() => loadSavedFilter(saved)}
                  className="flex-1 text-left text-sm text-gray-700 hover:text-blue-600"
                >
                  {saved?.name}
                </button>
                <button
                  onClick={() => deleteSavedFilter(saved?.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* User Activity Summary */}
      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Atividade dos Usuários</h3>
        <div className="space-y-2">
          {userActivity?.slice(0, 5)?.map(user => (
            <div key={user?.user_id} className="p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-900">{user?.user_email}</span>
                <span className="text-xs text-gray-500">{user?.total_actions} ações</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span className="text-green-600">✓ {user?.creates_count}</span>
                <span className="text-blue-600">✏ {user?.updates_count}</span>
                <span className="text-red-600">✗ {user?.deletes_count}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;